import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, imageUrl, uploadFile } from '../lib/api';
import { Button } from './ui/button';

interface CoverUploadProps {
  imageId: string | null;
  onChange: (imageId: string | null) => void;
  label?: string;
}

/**
 * Picks a cover from disk or straight from the phone camera and hands the
 * stored image id back to the form. The server generates the thumbnails.
 */
export function CoverUpload({ imageId, onChange, label }: CoverUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);

    try {
      const image = await uploadFile<{ id: string }>('/images', file);
      onChange(image.id);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.uploadFailed'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const preview = imageUrl(imageId, 'medium');

  return (
    <div className="flex flex-col gap-2">
      {label ? <span className="text-sm font-medium text-muted">{label}</span> : null}

      <div className="flex items-start gap-3">
        <div className="aspect-[2/3] w-24 shrink-0 overflow-hidden rounded-lg border bg-surface-raised">
          {preview ? (
            <img src={preview} alt="" className="size-full object-cover" />
          ) : (
            <span className="grid size-full place-items-center halftone text-muted">
              <ImagePlus className="size-5" />
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            // capture lets the phone open the camera directly.
            capture="environment"
            className="hidden"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />

          <Button
            type="button"
            variant="surface"
            size="sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
            {t('form.chooseCover')}
          </Button>

          {imageId ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
              <Trash2 className="size-4" />
              {t('form.removeCover')}
            </Button>
          ) : null}

          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}
